import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  FileText,
  LayoutTemplate,
  Sparkles,
  Code2,
  CheckCircle,
  Zap,
  AlertCircle,
  ArrowRight,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Settings2,
  MessageSquare,
  Phone,
  ShieldCheck,
  Layers,
  Lock,
  Unlock,
  SlidersHorizontal,
  Check,
  HelpCircle,
  Hash,
  DollarSign,
  UploadCloud,
  Loader2
} from 'lucide-react';

export interface FormFieldDefinition {
  id: string;
  label: string;
  questionPrompt?: string;
  type?: 'text' | 'number' | 'currency' | 'date' | 'phone' | 'email' | 'select' | 'boolean' | 'textarea';
  options?: string[];
  required?: boolean;
  order?: number;
  placeholder?: string;
  helpText?: string;
}

export interface FormSectionDefinition {
  id: string;
  title: string;
  description?: string;
  order?: number;
  fields: FormFieldDefinition[];
}

export interface MultiFormDefinition {
  id: string;
  title: string;
  description?: string;
  order?: number;
  isActive?: boolean;
  sections?: FormSectionDefinition[];
  fields?: FormFieldDefinition[];
  ownerAlertPhone?: string;
  confirmationMessage?: string;
}

export interface FormGeneratorNodeData {
  documentTitle?: string;
  documentContent?: string;
  schemaType?: string;
  formTheme?: string;
  autoGenerateSchema?: boolean;
  isCustomizable?: boolean;

  // ── Multi-Step Questionnaire & Form Engine ──
  forms?: MultiFormDefinition[];
  activeFormId?: string;
  sections?: FormSectionDefinition[];
  fields?: FormFieldDefinition[];
  ownerAlertPhone?: string;
  enableStepByStepInterrogation?: boolean;
  enableMissedFieldRecovery?: boolean;
  requireCustomerConfirmation?: boolean;
  [key: string]: unknown;
}

// ── Built-In 1-Click Blueprints ──
const PRESET_BLUEPRINTS: Record<string, { title: string; schemaType: string; sections: FormSectionDefinition[] }> = {
  sri_lanka_tax_2026: {
    title: 'Sri Lanka Income Tax Assessment 2026',
    schemaType: 'tax_form',
    sections: [
      {
        id: 'sec_tax_identity',
        title: '1. Taxpayer Identification',
        description: 'Personal details and Tax Identification Number',
        order: 1,
        fields: [
          { id: 'full_name', label: 'Full Legal Name', questionPrompt: 'ඔබගේ සම්පූර්ණ නම (Full Name) සඳහන් කළ හැකිද?', type: 'text', required: true, order: 1 },
          { id: 'nic_passport', label: 'NIC or Passport Number', questionPrompt: 'ඔබගේ ජාතික හැඳුනුම්පත් අංකය (NIC) ලබාදිය හැකිද?', type: 'text', required: true, order: 2 },
          { id: 'tin_number', label: 'TIN (Taxpayer Identification No)', questionPrompt: 'ඔබගේ දේශීය ආදායම් දෙපාර්තමේන්තුවේ TIN අංකය තිබේද? තිබේ නම් සඳහන් කරන්න.', type: 'text', required: true, order: 3 },
          { id: 'contact_phone', label: 'Contact WhatsApp Phone', questionPrompt: 'ඔබව සම්බන්ධ කරගත හැකි WhatsApp දුරකථන අංකය කුමක්ද?', type: 'phone', required: true, order: 4 },
        ]
      },
      {
        id: 'sec_tax_income',
        title: '2. Annual Income Sources',
        description: 'Employment, Business, and Investment earnings for the tax year',
        order: 2,
        fields: [
          { id: 'employment_type', label: 'Primary Income Category', questionPrompt: 'ඔබගේ ප්‍රධාන ආදායම් මාර්ගය කුමක්ද? (Salaried Employee, Business Owner, Freelancer)?', type: 'select', options: ['Salaried Employee', 'Business Owner', 'Freelancer / Independent', 'Rent / Investment'], required: true, order: 1 },
          { id: 'gross_annual_income', label: 'Gross Annual Income (LKR)', questionPrompt: 'පසුගිය වසර සඳහා ඔබගේ මුළු වාර්ෂික ආදායම (Gross Annual Income) දළ වශයෙන් කීයද?', type: 'currency', required: true, order: 2 },
          { id: 'other_earnings', label: 'Other Earnings / Bonuses (LKR)', questionPrompt: 'වෙනත් අමතර ආදායම් හෝ බෝනස් ලැබුණාද? තිබේ නම් අගය සඳහන් කරන්න.', type: 'currency', required: false, order: 3 },
        ]
      },
      {
        id: 'sec_tax_deductions',
        title: '3. Reliefs & Qualifying Deductions',
        description: 'Loan interest, insurance, and medical claims',
        order: 3,
        fields: [
          { id: 'housing_loan_interest', label: 'Housing Loan Interest (LKR)', questionPrompt: 'නිවාස ණය සඳහා පොලී ගෙවීම් තිබේද? (තිබේ නම් අගය සඳහන් කරන්න)', type: 'currency', required: false, order: 1 },
          { id: 'life_medical_insurance', label: 'Insurance Premiums (LKR)', questionPrompt: 'ජීවිත හෝ වෛද්‍ය රක්ෂණ වාරික ගෙවා තිබේද?', type: 'currency', required: false, order: 2 },
          { id: 'declaration_confirmed', label: 'Applicant Declaration', questionPrompt: 'ඉහත ලබාදුන් තොරතුරු සත්‍ය හා නිවැරදි බව ඔබ තහවුරු කරනවාද?', type: 'boolean', required: true, order: 3 },
        ]
      }
    ]
  },
  kyc_onboarding: {
    title: 'Customer KYC & Client Onboarding Intake',
    schemaType: 'lead_intake',
    sections: [
      {
        id: 'sec_kyc_personal',
        title: '1. Personal Profile & Identification',
        order: 1,
        fields: [
          { id: 'client_name', label: 'Full Name', questionPrompt: 'ඔබගේ සම්පූර්ණ නම කුමක්ද?', type: 'text', required: true, order: 1 },
          { id: 'client_id', label: 'National ID / Passport', questionPrompt: 'ඔබගේ ජාතික හැඳුනුම්පත් අංකය ලබාදෙන්න.', type: 'text', required: true, order: 2 },
          { id: 'residential_address', label: 'Residential Address', questionPrompt: 'ඔබගේ ස්ථිර පදිංචි ලිපිනය කුමක්ද?', type: 'text', required: true, order: 3 },
          { id: 'email_address', label: 'Email Address', questionPrompt: 'ඔබගේ ඊමේල් ලිපිනය කුමක්ද?', type: 'email', required: false, order: 4 },
        ]
      },
      {
        id: 'sec_kyc_occupation',
        title: '2. Professional Profile & Source of Funds',
        order: 2,
        fields: [
          { id: 'occupation', label: 'Occupation / Business', questionPrompt: 'ඔබගේ වෘත්තිය හෝ ව්‍යාපාරයේ ස්වභාවය කුමක්ද?', type: 'text', required: true, order: 1 },
          { id: 'monthly_turnover', label: 'Estimated Monthly Turnover', questionPrompt: 'මාසික දළ ආදායම හෝ පිරිවැටුම සාමාන්‍යයෙන් කීයද?', type: 'currency', required: true, order: 2 },
        ]
      }
    ]
  },
  loan_application: {
    title: 'Credit & Financial Assistance Application',
    schemaType: 'loan_application',
    sections: [
      {
        id: 'sec_loan_borrower',
        title: '1. Borrower Profile',
        order: 1,
        fields: [
          { id: 'borrower_name', label: 'Borrower Name', questionPrompt: 'ඉල්ලුම්කරුගේ සම්පූර්ණ නම ලබාදෙන්න.', type: 'text', required: true, order: 1 },
          { id: 'phone_number', label: 'Mobile Number', questionPrompt: 'දුරකථන අංකය කුමක්ද?', type: 'phone', required: true, order: 2 },
          { id: 'monthly_income', label: 'Monthly Fixed Income (LKR)', questionPrompt: 'ඔබගේ මාසික ස්ථාවර ආදායම කීයද?', type: 'currency', required: true, order: 3 },
        ]
      },
      {
        id: 'sec_loan_request',
        title: '2. Loan Request Specifications',
        order: 2,
        fields: [
          { id: 'requested_amount', label: 'Requested Loan Amount (LKR)', questionPrompt: 'ඔබට අවශ්‍ය ණය මුදල කීයද?', type: 'currency', required: true, order: 1 },
          { id: 'repayment_period_months', label: 'Repayment Term (Months)', questionPrompt: 'ණය පියවීමට අපේක්ෂිත මාස ගණන කීයද? (උදා: 12, 24, 36)?', type: 'number', required: true, order: 2 },
          { id: 'loan_purpose', label: 'Purpose of Loan', questionPrompt: 'ණය මුදල ලබාගැනීමේ ප්‍රධාන අරමුණ කුමක්ද?', type: 'text', required: true, order: 3 },
        ]
      }
    ]
  }
};

export const FormGeneratorNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as FormGeneratorNodeData;
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  const [activeTab, setActiveTab] = useState<'questionnaire' | 'document' | 'notifications'>('questionnaire');
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData, isCompactView, openInspector } = useTenantView();

  const isCustomizable = nodeData.isCustomizable !== false;
  const isLocked = isTenantView && !isCustomizable;

  // Resolved sections array
  const sections: FormSectionDefinition[] = useMemo(() => {
    if (Array.isArray(nodeData.sections) && nodeData.sections.length > 0) {
      return nodeData.sections;
    }
    // Default initial tax section if empty
    return PRESET_BLUEPRINTS.sri_lanka_tax_2026.sections;
  }, [nodeData.sections]);

  const activeSection = useMemo(() => {
    if (selectedSectionId) {
      const found = sections.find((s) => s.id === selectedSectionId);
      if (found) return found;
    }
    return sections[0] || null;
  }, [sections, selectedSectionId]);

  const persistNodeDataDirectly = (field: keyof FormGeneratorNodeData, value: any, allUpdates?: Partial<FormGeneratorNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'FORM_GENERATOR',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[FormGeneratorNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const [isExtracting, setIsExtracting] = useState<boolean>(false);
  const [extractStatus, setExtractStatus] = useState<string | null>(null);
  const [extractError, setExtractError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileUploadAndExtract = async (file: File) => {
    if (isLocked) return;
    if (!file) return;

    setIsExtracting(true);
    setExtractStatus('Luna analyzing document & extracting questionnaire...');
    setExtractError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (nodeData.documentTitle) {
        formData.append('documentTitle', nodeData.documentTitle);
      }

      const headers: Record<string, string> = {};
      const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

      const res = await fetch('/api/admin/luna-flows/extract-form-from-doc', {
        method: 'POST',
        headers,
        body: formData,
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Failed to extract form schema from document.');
      }

      const extracted = json.extractedForm;
      const newTitle = extracted.title || file.name.replace(/\.[^/.]+$/, '');
      const newSchemaType = extracted.schemaType || 'tax_form';
      const newSections = extracted.sections || [];

      handleChange('documentTitle', newTitle, {
        schemaType: newSchemaType,
        sections: newSections,
        autoGenerateSchema: true,
        documentContent: extracted.description || `Autonomously extracted from ${file.name}`
      });

      if (newSections.length > 0) {
        setSelectedSectionId(newSections[0].id);
      }

      setExtractStatus(`✨ Extracted ${newSections.length} sections & ${extracted.totalFields} questions!`);
      setTimeout(() => setExtractStatus(null), 5000);
    } catch (err: any) {
      console.error('[FormGeneratorNode] Extraction error:', err);
      setExtractError(err?.message || 'Extraction failed');
      setTimeout(() => setExtractError(null), 6000);
    } finally {
      setIsExtracting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleChange = (field: keyof FormGeneratorNodeData, value: any, allUpdates?: Partial<FormGeneratorNodeData>) => {
    if (isLocked) return;
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value, ...(allUpdates || {}) } } : n))
    );
    persistNodeDataDirectly(field, value, allUpdates);
  };

  // Section & Field manipulation helpers
  const handleAddSection = () => {
    if (isLocked) return;
    const newSecNum = sections.length + 1;
    const newSec: FormSectionDefinition = {
      id: `sec_${Date.now()}`,
      title: `${newSecNum}. New Form Section`,
      description: 'Section details and inquiries',
      order: newSecNum,
      fields: [
        {
          id: `field_${Date.now()}_1`,
          label: 'Primary Question',
          questionPrompt: 'කරුණාකර මෙම විස්තරය ලබාදෙන්න.',
          type: 'text',
          required: true,
          order: 1
        }
      ]
    };
    const updated = [...sections, newSec];
    setSelectedSectionId(newSec.id);
    handleChange('sections', updated);
  };

  const handleDeleteSection = (secId: string) => {
    if (isLocked || sections.length <= 1) return;
    const updated = sections.filter((s) => s.id !== secId);
    setSelectedSectionId(updated[0]?.id || null);
    handleChange('sections', updated);
  };

  const handleMoveSection = (idx: number, direction: 'up' | 'down') => {
    if (isLocked) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= sections.length) return;
    const updated = [...sections];
    const [moved] = updated.splice(idx, 1);
    updated.splice(targetIdx, 0, moved);
    updated.forEach((s, i) => { s.order = i + 1; });
    handleChange('sections', updated);
  };

  const handleUpdateSectionTitle = (secId: string, title: string) => {
    if (isLocked) return;
    const updated = sections.map((s) => (s.id === secId ? { ...s, title } : s));
    handleChange('sections', updated);
  };

  // Field manipulation inside active section
  const handleAddField = (secId: string) => {
    if (isLocked) return;
    const currentSec = sections.find((s) => s.id === secId);
    if (!currentSec) return;
    const newFieldNum = (currentSec.fields?.length || 0) + 1;
    const newField: FormFieldDefinition = {
      id: `field_${Date.now()}`,
      label: `Field #${newFieldNum}`,
      questionPrompt: 'කරුණාකර අදාළ තොරතුර ලබාදිය හැකිද?',
      type: 'text',
      required: true,
      order: newFieldNum
    };
    const updatedFields = [...(currentSec.fields || []), newField];
    const updatedSections = sections.map((s) => (s.id === secId ? { ...s, fields: updatedFields } : s));
    handleChange('sections', updatedSections);
  };

  const handleUpdateField = (secId: string, fieldId: string, updates: Partial<FormFieldDefinition>) => {
    if (isLocked) return;
    const updatedSections = sections.map((s) => {
      if (s.id !== secId) return s;
      const updatedFields = (s.fields || []).map((f) => (f.id === fieldId ? { ...f, ...updates } : f));
      return { ...s, fields: updatedFields };
    });
    handleChange('sections', updatedSections);
  };

  const handleDeleteField = (secId: string, fieldId: string) => {
    if (isLocked) return;
    const updatedSections = sections.map((s) => {
      if (s.id !== secId) return s;
      const updatedFields = (s.fields || []).filter((f) => f.id !== fieldId);
      return { ...s, fields: updatedFields };
    });
    handleChange('sections', updatedSections);
  };

  const handleMoveField = (secId: string, fieldIdx: number, direction: 'up' | 'down') => {
    if (isLocked) return;
    const currentSec = sections.find((s) => s.id === secId);
    if (!currentSec || !currentSec.fields) return;
    const targetIdx = direction === 'up' ? fieldIdx - 1 : fieldIdx + 1;
    if (targetIdx < 0 || targetIdx >= currentSec.fields.length) return;
    const updatedFields = [...currentSec.fields];
    const [moved] = updatedFields.splice(fieldIdx, 1);
    updatedFields.splice(targetIdx, 0, moved);
    updatedFields.forEach((f, i) => { f.order = i + 1; });
    const updatedSections = sections.map((s) => (s.id === secId ? { ...s, fields: updatedFields } : s));
    handleChange('sections', updatedSections);
  };

  const handleLoadBlueprint = (key: string) => {
    if (isLocked) return;
    const bp = PRESET_BLUEPRINTS[key];
    if (!bp) return;
    handleChange('documentTitle', bp.title, {
      schemaType: bp.schemaType,
      sections: bp.sections,
      autoGenerateSchema: true
    });
    setSelectedSectionId(bp.sections[0]?.id || null);
  };

  const totalFieldsCount = useMemo(() => {
    return sections.reduce((acc, s) => acc + (s.fields?.length || 0), 0);
  }, [sections]);

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  return (
    <div
      className={`group rounded-2xl border transition-all duration-300 w-[420px] select-none relative backdrop-blur-xl bg-gradient-to-b from-[#0A1628]/98 via-[#060D1A]/98 to-[#03060E]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-cyan-400 ring-2 ring-cyan-400/50 shadow-[0_0_35px_rgba(6,182,212,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-cyan-500/40 hover:border-cyan-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(6,182,212,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Quick Jump Toolbar ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('formLeadNode', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-cyan-950 via-slate-900 to-blue-950 border-2 border-cyan-400 text-cyan-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(6,182,212,0.8)] hover:border-cyan-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data to view submissions and leads"
          >
            <LayoutTemplate size={13} className="text-cyan-300 animate-pulse" />
            <span>View Submissions in Portal</span>
            <ArrowRight size={13} className="text-cyan-400" />
          </button>
        </div>
      )}

      {/* ── Directional Handles ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-form-doc"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Input from Luna Core Hub (out-actions)"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-form-gen"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Output to Luna Core Hub / Action Pipe"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-purple-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125 shadow-[0_0_8px_rgba(168,85,247,0.8)]"
      />

      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-cyan-950/80 via-blue-950/60 to-slate-950/80 px-4 py-3 border-b border-cyan-500/30 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 shadow-[0_0_12px_rgba(6,182,212,0.3)]">
            <LayoutTemplate size={16} />
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide flex items-center gap-1.5">
              <span>Form & Questionnaire</span>
              {isLocked && <span className="text-[10px] text-amber-400 font-mono font-bold">🔒 Locked</span>}
            </h4>
            <p className="text-[10px] text-cyan-300/70 font-mono">Dynamic Multi-Step Engine</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-cyan-400 animate-pulse bg-cyan-500/10 px-2 py-0.5 rounded-full border border-cyan-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
            {sections.length} SEC · {totalFieldsCount} FLDS
          </span>
        </div>
      </div>

      {/* ── Wiring Status ── */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Wired to Luna Core
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE ENGINE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-amber-400" /> Disconnected
            </span>
            <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      {/* ── Hidden Multimodal Document / Image File Input ── */}
      <input
        type="file"
        ref={fileInputRef}
        disabled={isLocked || isExtracting}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFileUploadAndExtract(f);
        }}
        accept="image/*,application/pdf,.doc,.docx,.txt"
        className="hidden"
      />

      {/* ── Body ── */}
      {isCompactView ? (
        <div className="p-4 space-y-2.5 text-xs">
          <div className="p-3 rounded-2xl bg-slate-900/80 border border-cyan-500/25 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText size={14} className="text-cyan-400" />
                <span className="font-bold text-white text-xs truncate max-w-[210px]">
                  {nodeData.documentTitle || 'Autonomous Questionnaire'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => openInspector && openInspector(id)}
                className="nodrag px-2.5 py-1 rounded-lg text-[10px] font-bold bg-cyan-500/20 hover:bg-cyan-500/35 text-cyan-200 border border-cyan-400/40 transition cursor-pointer active:scale-95 shadow-sm"
              >
                Inspect ⚙️
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 text-[10px] font-mono">
              <div className="p-2 rounded-xl bg-slate-950/70 border border-slate-800 text-slate-300">
                <span className="text-slate-500 block">Sections:</span>
                <span className="font-bold text-cyan-300">{sections.length} Active Steps</span>
              </div>
              <div className="p-2 rounded-xl bg-slate-950/70 border border-slate-800 text-slate-300">
                <span className="text-slate-500 block">Questions:</span>
                <span className="font-bold text-emerald-300">{totalFieldsCount} Form Fields</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1 text-[10px] text-slate-400">
              <span className="flex items-center gap-1">
                <Phone size={11} className="text-emerald-400" /> WhatsApp Owner Alert:
              </span>
              <span className="font-mono text-emerald-300 font-bold">
                {nodeData.ownerAlertPhone ? `+${nodeData.ownerAlertPhone.replace(/\D/g, '')}` : 'Store Manager Default'}
              </span>
            </div>

            {/* 1-Click Multimodal Doc/Image Extraction in Compact View */}
            <div className="pt-2 border-t border-slate-800/80 flex items-center gap-2">
              <button
                type="button"
                disabled={isLocked || isExtracting}
                onClick={() => fileInputRef.current?.click()}
                className="nodrag flex-1 py-1.5 px-2.5 rounded-xl text-[10px] font-bold bg-gradient-to-r from-purple-900/60 to-cyan-900/60 hover:from-purple-800/80 hover:to-cyan-800/80 text-cyan-200 border border-purple-500/30 hover:border-cyan-400 flex items-center justify-center gap-1.5 transition cursor-pointer active:scale-95 disabled:opacity-50"
                title="Upload document or image to build form"
              >
                {isExtracting ? (
                  <>
                    <Loader2 size={12} className="animate-spin text-cyan-300" />
                    <span>Extracting...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={12} className="text-purple-300 animate-pulse" />
                    <span>Build from Doc / Image</span>
                    <UploadCloud size={12} className="text-cyan-300" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-3.5 text-xs">
          {/* Form Title & Blueprint Quick Selector */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <FileText size={12} className="text-cyan-400" /> Form Name / Title
              </label>
              {!isLocked && (
                <div className="flex items-center gap-1">
                  <span className="text-[9px] text-slate-500 uppercase font-mono">Presets:</span>
                  <select
                    className="nodrag text-[9px] bg-slate-900 border border-slate-700 text-cyan-300 rounded px-1.5 py-0.5 focus:outline-none cursor-pointer"
                    onChange={(e) => {
                      if (e.target.value) {
                        handleLoadBlueprint(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>Load 1-Click Template...</option>
                    <option value="sri_lanka_tax_2026">📋 Tax Assessment 2026</option>
                    <option value="kyc_onboarding">👤 KYC & Client Intake</option>
                    <option value="loan_application">💳 Loan Application</option>
                  </select>
                </div>
              )}
            </div>
            <input
              type="text"
              disabled={isLocked}
              className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-cyan-400 font-bold disabled:opacity-60"
              placeholder="e.g. Income Tax Declaration 2026 / Client KYC Intake"
              value={nodeData.documentTitle || ''}
              onChange={(e) => handleChange('documentTitle', e.target.value)}
            />

            {/* AI Multimodal Auto-Extraction Trigger */}
            <div className="space-y-1.5 pt-1">
              <button
                type="button"
                disabled={isLocked || isExtracting}
                onClick={() => fileInputRef.current?.click()}
                className="nodrag w-full py-2 px-3 rounded-xl bg-gradient-to-r from-purple-900/70 via-indigo-900/70 to-cyan-900/70 hover:from-purple-800/90 hover:via-indigo-800/90 hover:to-cyan-800/90 border border-purple-500/40 hover:border-cyan-400 text-slate-100 text-xs font-bold flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(168,85,247,0.3)] transition cursor-pointer active:scale-[0.98] disabled:opacity-50"
                title="Upload tax form photo, scanned PDF, or document to auto-generate questionnaire"
              >
                {isExtracting ? (
                  <>
                    <Loader2 size={14} className="animate-spin text-cyan-300" />
                    <span className="text-cyan-200">Luna AI Analyzing Document...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={14} className="text-purple-300 animate-pulse" />
                    <span>Build Form from Doc / Image (AI Auto-Extract)</span>
                    <UploadCloud size={14} className="text-cyan-300" />
                  </>
                )}
              </button>

              {extractStatus && (
                <div className="p-2 rounded-xl bg-cyan-950/70 border border-cyan-400/40 text-cyan-300 text-[11px] font-mono flex items-center gap-2">
                  <Sparkles size={12} className="text-cyan-400" />
                  <span>{extractStatus}</span>
                </div>
              )}
              {extractError && (
                <div className="p-2 rounded-xl bg-rose-950/70 border border-rose-500/40 text-rose-300 text-[11px] font-mono flex items-center gap-2">
                  <AlertCircle size={12} className="text-rose-400" />
                  <span>{extractError}</span>
                </div>
              )}
            </div>
          </div>

          {/* Sub-Tab Navigation */}
          <div className="flex items-center gap-1 p-1 bg-black/40 rounded-xl border border-white/5 text-[10px]">
            <button
              type="button"
              onClick={() => setActiveTab('questionnaire')}
              className={`flex-1 py-1.5 rounded-lg font-bold flex items-center justify-center gap-1.5 transition ${
                activeTab === 'questionnaire'
                  ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <LayoutTemplate size={12} />
              <span>Step-by-Step Questions</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('document')}
              className={`flex-1 py-1.5 rounded-lg font-bold flex items-center justify-center gap-1.5 transition ${
                activeTab === 'document'
                  ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Code2 size={12} />
              <span>Rules Blueprint</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('notifications')}
              className={`flex-1 py-1.5 rounded-lg font-bold flex items-center justify-center gap-1.5 transition ${
                activeTab === 'notifications'
                  ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Phone size={12} />
              <span>Owner Alert</span>
            </button>
          </div>

          {/* TAB 1: Step-by-Step Multi-Section Questionnaire Builder */}
          {activeTab === 'questionnaire' && (
            <div className="space-y-3">
              {/* Sections Header with Add Button */}
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <Layers size={11} className="text-cyan-400" /> Form Sections ({sections.length})
                </span>
                {!isLocked && (
                  <button
                    type="button"
                    onClick={handleAddSection}
                    className="nodrag flex items-center gap-1 px-2 py-0.5 rounded-md bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/30 text-[10px] font-bold cursor-pointer transition"
                  >
                    <Plus size={10} /> Add Section
                  </button>
                )}
              </div>

              {/* Section Tabs */}
              <div className="flex gap-1.5 overflow-x-auto pb-1 nodrag nowheel">
                {sections.map((sec, sIdx) => {
                  const isCur = activeSection?.id === sec.id;
                  return (
                    <button
                      key={sec.id}
                      type="button"
                      onClick={() => setSelectedSectionId(sec.id)}
                      className={`px-2.5 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap transition border ${
                        isCur
                          ? 'bg-cyan-500/30 text-cyan-200 border-cyan-400 shadow-[0_0_12px_rgba(6,182,212,0.4)]'
                          : 'bg-slate-900/80 text-slate-400 border-slate-800 hover:text-slate-200'
                      }`}
                    >
                      {sec.title || `Section ${sIdx + 1}`} ({sec.fields?.length || 0})
                    </button>
                  );
                })}
              </div>

              {/* Active Section Editor */}
              {activeSection && (
                <div className="p-3 rounded-xl bg-slate-950/80 border border-cyan-500/30 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <input
                      type="text"
                      disabled={isLocked}
                      value={activeSection.title}
                      onChange={(e) => handleUpdateSectionTitle(activeSection.id, e.target.value)}
                      className="nodrag bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-xs text-white font-bold flex-1 focus:outline-none focus:border-cyan-400"
                      placeholder="Section Title..."
                    />
                    {!isLocked && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleMoveSection(sections.findIndex((s) => s.id === activeSection.id), 'up')}
                          className="nodrag p-1 rounded bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white"
                          title="Move section up"
                        >
                          <ChevronUp size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveSection(sections.findIndex((s) => s.id === activeSection.id), 'down')}
                          className="nodrag p-1 rounded bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white"
                          title="Move section down"
                        >
                          <ChevronDown size={12} />
                        </button>
                        {sections.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleDeleteSection(activeSection.id)}
                            className="nodrag p-1 rounded bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 border border-rose-800/40"
                            title="Delete section"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Fields Inside Active Section */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between pt-1 border-t border-slate-800 text-[10px] text-slate-400">
                      <span>Questions / Fields ({activeSection.fields?.length || 0})</span>
                      {!isLocked && (
                        <button
                          type="button"
                          onClick={() => handleAddField(activeSection.id)}
                          className="nodrag flex items-center gap-1 text-cyan-300 hover:text-cyan-200 font-bold"
                        >
                          <Plus size={11} /> Add Question
                        </button>
                      )}
                    </div>

                    <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1 nodrag nowheel">
                      {(activeSection.fields || []).map((f, fIdx) => (
                        <div
                          key={f.id}
                          className="p-2.5 rounded-xl bg-slate-900/90 border border-white/5 space-y-2 hover:border-cyan-500/30 transition"
                        >
                          <div className="flex items-center justify-between gap-1.5">
                            <span className="w-5 h-5 rounded-full bg-cyan-950 border border-cyan-500/40 text-cyan-300 flex items-center justify-center text-[10px] font-mono font-bold">
                              {fIdx + 1}
                            </span>
                            <input
                              type="text"
                              disabled={isLocked}
                              value={f.label}
                              onChange={(e) => handleUpdateField(activeSection.id, f.id, { label: e.target.value })}
                              placeholder="Field Label (e.g. TIN Number)"
                              className="nodrag bg-slate-950 border border-slate-800 rounded px-2 py-1 text-[11px] text-white flex-1 font-semibold focus:outline-none focus:border-cyan-400"
                            />
                            <select
                              disabled={isLocked}
                              value={f.type || 'text'}
                              onChange={(e) => handleUpdateField(activeSection.id, f.id, { type: e.target.value as any })}
                              className="nodrag bg-slate-950 border border-slate-800 rounded px-1.5 py-1 text-[10px] text-cyan-300 focus:outline-none"
                            >
                              <option value="text">Text</option>
                              <option value="number">Number</option>
                              <option value="currency">Currency (LKR)</option>
                              <option value="date">Date</option>
                              <option value="phone">Phone</option>
                              <option value="email">Email</option>
                              <option value="select">Dropdown</option>
                              <option value="boolean">Yes/No</option>
                            </select>
                            {!isLocked && (
                              <div className="flex items-center gap-0.5">
                                <button
                                  type="button"
                                  onClick={() => handleMoveField(activeSection.id, fIdx, 'up')}
                                  className="nodrag p-0.5 text-slate-500 hover:text-slate-200"
                                >
                                  <ChevronUp size={11} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleMoveField(activeSection.id, fIdx, 'down')}
                                  className="nodrag p-0.5 text-slate-500 hover:text-slate-200"
                                >
                                  <ChevronDown size={11} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteField(activeSection.id, f.id)}
                                  className="nodrag p-0.5 text-rose-400 hover:text-rose-300"
                                >
                                  <Trash2 size={11} />
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Conversational Prompt to Ask the User */}
                          <div>
                            <div className="flex items-center justify-between text-[9px] text-slate-400 mb-1">
                              <span className="flex items-center gap-1 font-mono text-cyan-300">
                                <MessageSquare size={10} /> Luna Conversational Prompt:
                              </span>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  disabled={isLocked}
                                  checked={f.required !== false}
                                  onChange={(e) => handleUpdateField(activeSection.id, f.id, { required: e.target.checked })}
                                  className="accent-cyan-500 rounded"
                                />
                                <span className={f.required !== false ? 'text-cyan-300 font-bold' : 'text-slate-500'}>
                                  Required
                                </span>
                              </label>
                            </div>
                            <input
                              type="text"
                              disabled={isLocked}
                              value={f.questionPrompt || ''}
                              onChange={(e) => handleUpdateField(activeSection.id, f.id, { questionPrompt: e.target.value })}
                              placeholder="e.g. ඔබගේ ආදායම් බදු අංකය (TIN) සඳහන් කරන්න පුළුවන්ද?"
                              className="nodrag w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-[10px] text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: Raw Document Blueprint */}
          {activeTab === 'document' && (
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
                  <Code2 size={12} className="text-cyan-400" /> Document Content & Assessment Rules
                </label>
                <textarea
                  rows={6}
                  disabled={isLocked}
                  className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-cyan-400 resize-none font-sans leading-relaxed disabled:opacity-60"
                  placeholder="Paste tax brackets, qualifying relief formulas, or statutory assessment guidelines..."
                  value={nodeData.documentContent || ''}
                  onChange={(e) => handleChange('documentContent', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
                  <Sparkles size={12} className="text-cyan-400" /> GenUI Schema Mode
                </label>
                <select
                  disabled={isLocked}
                  className="nodrag w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-cyan-400 cursor-pointer font-medium disabled:opacity-60"
                  value={nodeData.schemaType || 'tax_form'}
                  onChange={(e) => handleChange('schemaType', e.target.value)}
                >
                  <option value="tax_form">📋 Tax & Financial Calculation Form</option>
                  <option value="lead_intake">👤 User Profile & KYC Intake</option>
                  <option value="loan_application">💳 Loan & Credit Application</option>
                  <option value="auto">✨ Autonomous AI Schema Ingestion</option>
                  <option value="custom">⚙️ Custom Form Blueprint</option>
                </select>
              </div>
            </div>
          )}

          {/* TAB 3: Notifications & Owner WhatsApp Dispatch */}
          {activeTab === 'notifications' && (
            <div className="space-y-3">
              <div className="p-3 rounded-xl bg-emerald-950/20 border border-emerald-500/30 space-y-2">
                <label className="block text-[10px] font-black uppercase tracking-wider text-emerald-300 flex items-center gap-1.5">
                  <Phone size={12} className="text-emerald-400" /> Store Owner / Manager WhatsApp Destination
                </label>
                <input
                  type="text"
                  disabled={isLocked}
                  className="nodrag nowheel w-full bg-slate-900/90 border border-emerald-500/30 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-emerald-400 font-mono"
                  placeholder="+94771234567 (Blank = Use Default Store Phone)"
                  value={nodeData.ownerAlertPhone || ''}
                  onChange={(e) => handleChange('ownerAlertPhone', e.target.value)}
                />
                <p className="text-[10px] text-emerald-400/80 font-mono">
                  ⚡ When the customer confirms their answers, Luna automatically formats and sends the entire completed questionnaire to this WhatsApp number.
                </p>
              </div>

              <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-bold text-white block text-[11px]">2-Step Customer Confirmation</span>
                    <span className="text-[10px] text-slate-400">Summarize answers and require explicit customer confirmation before sending</span>
                  </div>
                  <input
                    type="checkbox"
                    disabled={isLocked}
                    checked={nodeData.requireCustomerConfirmation !== false}
                    onChange={(e) => handleChange('requireCustomerConfirmation', e.target.checked)}
                    className="w-4 h-4 accent-cyan-500 rounded cursor-pointer"
                  />
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                  <div>
                    <span className="font-bold text-white block text-[11px]">Missed Question Recovery</span>
                    <span className="text-[10px] text-slate-400">Automatically re-ask skipped or vague mandatory questions</span>
                  </div>
                  <input
                    type="checkbox"
                    disabled={isLocked}
                    checked={nodeData.enableMissedFieldRecovery !== false}
                    onChange={(e) => handleChange('enableMissedFieldRecovery', e.target.checked)}
                    className="w-4 h-4 accent-cyan-500 rounded cursor-pointer"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Dynamic State Indicator */}
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
              <CheckCircle size={11} className="text-cyan-400" /> Real-Time Engine:
            </span>
            <span className="text-[10px] font-mono text-cyan-300 bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-500/20">
              Active Multi-Step Schema
            </span>
          </div>
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!bg-cyan-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-bottom-2 transition-transform hover:scale-125"
      />
    </div>
  );
});

FormGeneratorNode.displayName = 'FormGeneratorNode';
export default FormGeneratorNode;
